import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { allMedia, filterMedia, getHotMedia } from "@/utils/media";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { categories } from "@/mocks/categories";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState("all");

  useDocumentTitle(
    keyword ? `“${keyword}”的搜索结果 - 91免费影视` : "搜索影视 - 91免费影视",
    "在 91免费影视 平台搜索电影、电视剧、综艺与动漫，快速定位想看的作品。",
  );

  useEffect(() => {
    setInput(keyword);
    setTypeFilter("all");
  }, [keyword]);

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    const matched = filterMedia(allMedia, { keyword });
    if (typeFilter === "all") return matched;
    return matched.filter((item) => item.type === typeFilter);
  }, [keyword, typeFilter]);

  const availableTypes = useMemo(() => {
    if (!keyword.trim()) return [];
    const matched = filterMedia(allMedia, { keyword });
    const slugs = Array.from(new Set(matched.map((item) => item.type)));
    return categories.filter((cat) => slugs.includes(cat.slug));
  }, [keyword]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setParams(value ? { q: value } : {});
  };

  const hot = getHotMedia(6);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="font-medium text-foreground-800">搜索结果</span>
      </nav>

      <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影视搜索</h1>

      <form onSubmit={handleSubmit} className="mt-5 max-w-2xl">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、地区或类型关键词..."
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </div>
      </form>

      {keyword.trim() ? (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-foreground-600">
              搜索关键词：<strong className="text-foreground-900">“{keyword}”</strong>，共找到{" "}
              <strong className="text-accent-600">{results.length}</strong> 个结果
            </p>
          </div>

          {availableTypes.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-1 rounded-full bg-secondary-100 p-1 sm:inline-flex">
              <button
                type="button"
                onClick={() => setTypeFilter("all")}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  typeFilter === "all"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                全部
              </button>
              {availableTypes.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setTypeFilter(cat.slug)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    typeFilter === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {results.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {results.map((item) => (
                <MediaCard key={item.id} media={item} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg bg-background-50 px-6 py-16 text-center ring-1 ring-background-200">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-700">
                <i className="ri-emotion-sad-line" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                没有找到与“{keyword}”相关的影视
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-500">
                换个关键词试试，例如“科幻”“古装”“悬疑”，或者浏览下方热门推荐。
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 flex flex-col items-start rounded-lg bg-primary-50 px-6 py-10 ring-1 ring-primary-100">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-xl text-background-50">
            <i className="ri-lightbulb-line" />
          </span>
          <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">开始你的搜索</h3>
          <p className="mt-2 text-sm text-foreground-600">
            在上方输入影视名称、地区或类型关键词，即可快速定位想看的作品。
          </p>
        </div>
      )}

      <section className="mt-12 border-t border-background-200 pt-10">
        <SectionHeading icon="ri-fire-line" title="热门推荐" desc="没有搜到？这些高分作品也许合你口味" moreTo="/category/movie" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {hot.map((item) => (
            <MediaCard key={item.id} media={item} />
          ))}
        </div>
      </section>
    </div>
  );
}